import {
  useRef,
  useState,
  useEffect,
  isValidElement,
  InputHTMLAttributes,
} from 'react';

// Components
import TooltipPopover, { TooltipType } from './TooltipPopover';

// UTILS
import { randomId } from '../utils';

// COMPONENT PROPS
interface TextboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label?: string;
  onChange?: (value: string, evt: React.ChangeEvent<HTMLInputElement>) => void;
  labelClassName?: string;
  inputClassName?: string;
  errorMessage?: string | null;
  isSearch?: boolean;
  tooltipType?: TooltipType;
  tooltip?: string | React.ReactElement;
}

// COMPONENT (TEXTBOX)
function Textbox({
  type,
  label,
  value,
  onChange,
  defaultValue,
  onBlur,
  labelClassName,
  inputClassName,
  errorMessage,
  tooltip,
  required,
  isSearch = false,
  tooltipType = 'info',
  ...props
}: TextboxProps) {
  // Input state (Uncontroled only)
  const [state, setState] = useState<JSX.IntrinsicElements['input']['value']>(
    defaultValue || ''
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const id = props.id ?? randomId('textbox');

  // Update input value
  const onUpdateValue = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { validationMessage } = evt.currentTarget;
    const inputVal = evt.currentTarget.value;

    if (onChange) {
      // Controlled
      onChange(inputVal, evt);

      // Uncontrolled
      const newValue = evt.currentTarget.value;
      setState(newValue);
    } else {
      // Uncontrolled
      const newValue = evt.currentTarget.value;
      setState(newValue);
    }

    if (errorMsg && !validationMessage) {
      setErrorMsg(null);
    }
  };

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.setCustomValidity(errorMessage || '');
    setErrorMsg(errorMessage || null);
    if (!errorMessage) setErrorMsg(null);
  }, [inputRef, errorMessage]);

  // Display error on blur
  const onValidate = (evt: React.FocusEvent<HTMLInputElement>) => {
    if (!required) return;

    let { validationMessage } = evt.currentTarget;
    // Set custom or default validation error message
    if (validationMessage && type === 'email') {
      validationMessage = 'Please enter a valid email';
    } else if (validationMessage.toLowerCase().includes('fill out')) {
      setErrorMsg('Required');
    } else if (validationMessage.toLowerCase().includes('format')) {
      setErrorMsg('Invalid format');
    } else if (validationMessage) {
      setErrorMsg(validationMessage);
    }

    // Clear validation error message if input is now valid
    if (errorMsg && !validationMessage) {
      setErrorMsg(null);
    }
  };

  const onBlurEvt = (evt: React.FocusEvent<HTMLInputElement>) => {
    const trimmed = evt.target.value.trim();
    if (onChange && trimmed !== value) {
      onChange(trimmed, evt);
    }
    if (onBlur) {
      onBlur(evt);
    }
    onValidate(evt);
  };

  // JSX
  return (
    <div
      className={`relative flex flex-col gap-1 ${props.className || ''}`.trim()}
    >
      {tooltip && !label ? (
        <TooltipPopover
          type={tooltipType}
          tooltipWidth="max-w-96"
          className="absolute top-0 -right-4"
          content={typeof tooltip === 'string' ? tooltip : undefined}
          complexContent={isValidElement(tooltip) ? tooltip : undefined}
        />
      ) : tooltip && label ? (
        <div className="w-fit relative">
          <TooltipPopover
            type={tooltipType}
            tooltipWidth="max-w-96"
            className="absolute top-0 -right-4"
            content={typeof tooltip === 'string' ? tooltip : undefined}
            complexContent={isValidElement(tooltip) ? tooltip : undefined}
          >
            <label
              className={`font-semibold leading-tight ${labelClassName}`}
              htmlFor={id}
            >
              {label}
              {required && (
                <span className="ms-0.5 text-status-red-500">*</span>
              )}
            </label>
          </TooltipPopover>
        </div>
      ) : label && !tooltip ? (
        <label className="font-semibold leading-tight" htmlFor={id}>
          {label}
          {required && <span className="ms-0.5 text-status-red-500">*</span>}
        </label>
      ) : null}
      {isSearch ? (
        <i className="icon-search absolute left-2 top-1/2 -translate-y-1/2 text-warm-black-600" />
      ) : null}
      <input
        ref={inputRef}
        id={id}
        {...props}
        className={`h-[38px] rounded border border-gray200 px-2 py-1.5 ${
          isSearch ? 'ps-6 ' : ''
        }outline-none focus:outline-none ${
          required ? 'focus:invalid:border-error ' : ''
        }disabled:bg-gray-300 disabled:text-gray-500 ${
          inputClassName || ''
        }`.trim()}
        type={type}
        onChange={onUpdateValue}
        value={value !== undefined ? value : state}
        aria-required={required}
        onBlur={onBlurEvt}
        required={required}
      />
      {errorMsg ? (
        <span className="text-sm leading-none text-error">{errorMsg}</span>
      ) : null}
    </div>
  );
}

export default Textbox;
